import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  DollarSign,
  ArrowRight,
  ExternalLink,
  Briefcase
} from 'lucide-react';
import JobCard from '../../components/Cards/JobCard';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const Jobs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSalary, setSelectedSalary] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // Mock jobs data
  const jobs = [
    {
      id: 1,
      title: 'Senior Python Developer',
      company_name: 'TechCorp India',
      location: 'Bangalore',
      job_type: 'full_time',
      salary_min: 800000,
      salary_max: 1200000,
      currency: 'INR',
      posted_date: '2024-01-15',
      skills_required: ['Python', 'Django', 'PostgreSQL', 'AWS'],
      external_url: 'https://example.com/job/1',
      similarity_score: 0.85,
      overall_score: 0.82,
      ai_insight: 'This role perfectly matches your Python expertise and backend development experience.',
      recommendation_rank: 1,
      fit_explanation: 'Job 1 matches your profile with 82% compatibility'
    },
    {
      id: 2,
      title: 'React Frontend Developer',
      company_name: 'StartupXYZ',
      location: 'Mumbai',
      job_type: 'full_time',
      salary_min: 600000,
      salary_max: 900000,
      currency: 'INR',
      posted_date: '2024-01-12',
      skills_required: ['React', 'JavaScript', 'TypeScript', 'Redux'],
      external_url: 'https://example.com/job/2',
      similarity_score: 0.78,
      overall_score: 0.75,
      ai_insight: 'Great opportunity to work with modern React stack and grow your frontend skills.',
      recommendation_rank: 2,
      fit_explanation: 'Job 2 matches your profile with 75% compatibility'
    },
    {
      id: 3,
      title: 'Full Stack Developer',
      company_name: 'Digital Solutions',
      location: 'Delhi',
      job_type: 'full_time',
      salary_min: 700000,
      salary_max: 1000000,
      currency: 'INR',
      posted_date: '2024-01-10',
      skills_required: ['Python', 'React', 'Node.js', 'MongoDB'],
      external_url: 'https://example.com/job/3',
      similarity_score: 0.72,
      overall_score: 0.78,
      ai_insight: 'Perfect role for someone with both frontend and backend skills.',
      recommendation_rank: 3,
      fit_explanation: 'Job 3 matches your profile with 78% compatibility'
    },
    {
      id: 4,
      title: 'Machine Learning Engineer',
      company_name: 'AI Innovations',
      location: 'Hyderabad',
      job_type: 'full_time',
      salary_min: 900000,
      salary_max: 1400000,
      currency: 'INR',
      posted_date: '2024-01-08',
      skills_required: ['Python', 'Machine Learning', 'TensorFlow', 'Data Science'],
      external_url: 'https://example.com/job/4',
      similarity_score: 0.68,
      overall_score: 0.70,
      ai_insight: 'Exciting opportunity to work on cutting-edge AI projects.',
      recommendation_rank: 4,
      fit_explanation: 'Job 4 matches your profile with 70% compatibility'
    }
  ];

  const locations = ['all', 'Bangalore', 'Mumbai', 'Delhi', 'Hyderabad', 'Chennai', 'Pune'];
  const jobTypes = ['all', 'full_time', 'part_time', 'contract', 'internship'];
  const salaryRanges = [
    { value: 'all', label: 'All Salaries' },
    { value: '0-500000', label: 'Below 5L' },
    { value: '500000-1000000', label: '5L - 10L' },
    { value: '1000000-1500000', label: '10L - 15L' },
    { value: '1500000+', label: 'Above 15L' }
  ];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.skills_required.some(skill => 
                           skill.toLowerCase().includes(searchTerm.toLowerCase())
                         );
    const matchesLocation = selectedLocation === 'all' || job.location === selectedLocation;
    const matchesType = selectedType === 'all' || job.job_type === selectedType;
    
    let matchesSalary = true;
    if (selectedSalary !== 'all') {
      const [min, max] = selectedSalary.split('-').map(Number);
      if (max) {
        matchesSalary = job.salary_min >= min && job.salary_max <= max;
      } else {
        matchesSalary = job.salary_min >= min;
      }
    }
    
    return matchesSearch && matchesLocation && matchesType && matchesSalary;
  });

  const handleApply = (job) => {
    console.log('Applying to job:', job.title);
    // Handle job application logic
  };

  const handleViewDetails = (job) => {
    console.log('Viewing job details:', job.title);
    // Handle view details logic
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Job Opportunities</h1>
          <p className="text-gray-600 mt-2">Find your next career opportunity with AI-powered matching</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className="btn btn-outline btn-sm">
            <ExternalLink className="h-4 w-4 mr-2" />
            View All Jobs
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-content">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search jobs, companies, or skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            {/* Location Filter */}
            <div className="lg:w-48">
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="input w-full"
              >
                {locations.map(location => (
                  <option key={location} value={location}>
                    {location === 'all' ? 'All Locations' : location}
                  </option>
                ))}
              </select>
            </div>

            {/* Job Type Filter */}
            <div className="lg:w-48">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="input w-full"
              >
                {jobTypes.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Salary Filter */}
            <div className="lg:w-48">
              <select
                value={selectedSalary}
                onChange={(e) => setSelectedSalary(e.target.value)}
                className="input w-full"
              >
                {salaryRanges.map(range => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Button */}
            <button className="btn btn-outline">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredJobs.length} of {jobs.length} jobs
        </p>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <select className="input text-sm">
            <option value="relevance">Relevance</option>
            <option value="salary">Salary</option>
            <option value="date">Date Posted</option>
            <option value="company">Company</option>
          </select>
        </div>
      </div>

      {/* Job List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredJobs.map((job, index) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <JobCard
                job={job}
                onApply={handleApply}
                onViewDetails={handleViewDetails}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Load More */}
      {filteredJobs.length > 0 && (
        <div className="text-center">
          <button className="btn btn-outline">
            Load More Jobs
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredJobs.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedLocation('all');
              setSelectedType('all');
              setSelectedSalary('all');
            }}
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default Jobs;
